import { defineBlock, cssText, resolveValue, type ConditionGroup } from "@broadpaper/core";

export const containerBlock = defineBlock<{ padding: number; gap: number | null }>({
  type: "container",
  name: "Container",
  description: "Groups blocks with shared padding and background",
  category: "layout",
  icon: "square-dashed",
  keywords: ["group", "box", "wrapper"],
  defaultProps: { padding: 0, gap: null },
  container: { accepts: ["@blocks", "@rows"] },
  split: "children",
  styleGroups: ["spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Container",
      fields: [
        { kind: "number", prop: "padding", label: "Padding", min: 0, max: 120, unit: "px" },
        { kind: "number", prop: "gap", label: "Block gap", min: 0, max: 120, unit: "px", description: "Overrides the document block gap" }
      ]
    }
  ],
  render: ({ h, css, children, styles, props, node, mode }) =>
    h(
      "div",
      { class: "bp-container", style: cssText({ ...css, padding: props.padding ? `${props.padding}px` : css["padding"] }) },
      h("div", { class: "bp-stack", style: { gap: `${props.gap ?? styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, children.length === 0 && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop blocks here") : children)
    )
});

export const cardBlock = defineBlock<{ title: string; padding: number | null; accent: boolean; accentColor: string }>({
  type: "card",
  name: "Card",
  description: "Panel with border, background and optional title",
  category: "layout",
  icon: "panel-top",
  keywords: ["panel", "box", "surface"],
  defaultProps: { title: "", padding: null, accent: false, accentColor: "$colors.primary" },
  container: { accepts: ["@blocks", "@rows"] },
  defaultFlow: { keepTogether: true },
  split: "children",
  styleGroups: ["spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Card",
      fields: [
        { kind: "text", prop: "title", label: "Title" },
        { kind: "number", prop: "padding", label: "Padding", min: 0, max: 120, unit: "px" },
        { kind: "toggle", prop: "accent", label: "Accent bar" },
        { kind: "color", prop: "accentColor", label: "Accent colour", when: (p) => !!p.accent }
      ]
    }
  ],
  canvas: { label: (p) => p.title || "Card" },
  render: ({ h, css, children, styles, props, theme, text, node, mode }) => {
    const card = styles.card;
    const pad = props.padding ?? card.padding ?? 16;
    const style: Record<string, string | undefined> = {
      ...css,
      background: css["background"] ?? resolveValue(card.background, theme, theme.colors.surface),
      border: css["border"] ?? `${card.borderWidth ?? 1}px solid ${resolveValue(card.borderColor, theme, theme.colors.border)}`,
      "border-radius": css["border-radius"] ?? `${card.borderRadius ?? theme.radius ?? 6}px`,
      padding: `${pad}px`,
      "box-shadow": card.shadow ? "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.1)" : undefined,
      "border-top": props.accent ? `3px solid ${resolveValue(props.accentColor, theme, theme.colors.primary)}` : undefined
    };
    return h(
      "div",
      { class: "bp-card", style: cssText(style) },
      props.title ? h("div", { class: "bp-card__title", style: { "font-family": theme.typography.headingFont, "font-weight": "600", "font-size": "1.1em", "margin-bottom": "8px", color: theme.colors.primary } }, text("title")) : null,
      h("div", { class: "bp-stack", style: { gap: `${styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, children.length === 0 && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop blocks here") : children)
    );
  }
});

export const pageBreakBlock = defineBlock<Record<string, never>>({
  type: "pageBreak",
  name: "Page break",
  description: "Start the following content on a new page",
  category: "layout",
  icon: "scissors",
  keywords: ["break", "new page"],
  defaultProps: {},
  defaultFlow: { breakAfter: "page" },
  split: "none",
  styleGroups: [],
  render: ({ h, mode }) => (mode === "design" ? h("div", { class: "bp-page-break" }, h("span", { class: "bp-page-break__label" }, "Page break")) : h("div", { class: "bp-page-break bp-page-break--print", style: { height: "0" } }))
});

export const keepTogetherBlock = defineBlock<{ label: string }>({
  type: "keepTogether",
  name: "Keep together",
  description: "Blocks inside never split across pages",
  category: "layout",
  icon: "link",
  keywords: ["group", "no split", "together"],
  defaultProps: { label: "" },
  container: { accepts: ["@blocks", "@rows"] },
  defaultFlow: { keepTogether: true },
  split: "children",
  styleGroups: ["spacing", "flow"],
  render: ({ h, css, children, styles, node, mode }) => h("div", { class: "bp-keep-together", style: cssText(css) }, h("div", { class: "bp-stack", style: { gap: `${styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, children.length === 0 && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop blocks here") : children))
});

/* ------------------------------------------------------------------ */
/* Conditional & repeater                                              */
/* ------------------------------------------------------------------ */

export const conditionalBlock = defineBlock<{ condition: string | ConditionGroup; label: string }>({
  type: "conditional",
  name: "Conditional",
  description: "Show the blocks inside only when a rule is true",
  category: "data",
  icon: "git-branch",
  keywords: ["if", "condition", "rule", "visibility", "show when"],
  defaultProps: () => ({ condition: { op: "and", rules: [] }, label: "" }),
  container: { accepts: ["@blocks", "@rows"], conditional: { conditionProp: "condition" } },
  split: "children",
  styleGroups: ["spacing", "flow"],
  bindings: [{ prop: "condition", kind: "condition" }],
  inspector: [{ title: "Show when", fields: [{ kind: "condition", prop: "condition", label: "Rule", fullWidth: true }] }],
  canvas: { label: (p) => p.label || "Conditional" },
  render: ({ h, css, children, styles, props, node, mode }) => {
    const result = (props as Record<string, unknown>).__conditionResult !== false;
    if (mode === "preview" && !result) return null;
    return h(
      "div",
      { class: ["bp-conditional", !result && "bp-conditional--false"], style: cssText(css) },
      mode === "design" ? h("div", { class: "bp-conditional__badge", contenteditable: "false" }, result ? "Shown when rule is true" : "Hidden with sample data") : null,
      h("div", { class: "bp-stack", style: { gap: `${styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, children.length === 0 && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop conditional blocks here") : children)
    );
  }
});

export const repeaterBlock = defineBlock<{ source: string; alias: string; keepItemTogether: boolean; gap: number | null; limit: number | null; emptyMessage: string; separator: boolean }>({
  type: "repeater",
  name: "Repeater",
  description: "Repeats the blocks inside for every item in a list",
  category: "data",
  icon: "repeat",
  keywords: ["loop", "for each", "list", "items"],
  defaultProps: { source: "", alias: "item", keepItemTogether: true, gap: null, limit: null, emptyMessage: "", separator: false },
  container: { accepts: ["@blocks", "@rows"], repeat: { collectionProp: "source", aliasProp: "alias", keepItemTogetherProp: "keepItemTogether", limitProp: "limit" } },
  split: "children",
  styleGroups: ["spacing", "flow"],
  bindings: [{ prop: "source", kind: "collection", alias: "alias" }, { prop: "emptyMessage", kind: "template" }],
  inspector: [
    {
      title: "Repeat",
      fields: [
        { kind: "collection", prop: "source", label: "For each item in", placeholder: "Pick a list" },
        { kind: "text", prop: "alias", label: "Item name", description: (p) => `The name each item goes by inside the repeater, e.g. {{ ${typeof p.alias === "string" && p.alias.trim() ? p.alias.trim() : "item"}.name }}`, allowTokens: false },
        { kind: "toggle", prop: "keepItemTogether", label: "Keep each item on one page" },
        { kind: "toggle", prop: "separator", label: "Divider between items" },
        { kind: "number", prop: "gap", label: "Gap between items", min: 0, max: 120, unit: "px" },
        { kind: "number", prop: "limit", label: "Maximum items", min: 1, max: 10000 },
        { kind: "text", prop: "emptyMessage", label: "Message when empty", description: "Printed instead of the items when the list is empty.", fullWidth: true }
      ]
    }
  ],
  canvas: { label: (p) => (p.source ? `Repeat ${p.source}` : "Repeater") },
  render: ({ h, css, children, styles, props, node, mode, theme, text }) => {
    const count = Number((props as Record<string, unknown>).__itemCount ?? 0);
    const b = node.bindings.source;
    if (children.length === 0 && mode === "preview") {
      return props.emptyMessage ? h("div", { class: "bp-repeater__empty", style: cssText({ ...css, color: theme.colors.mutedText }) }, text("emptyMessage")) : null;
    }
    const gap = props.gap ?? styles.spacing.blockGap;
    const items = props.separator
      ? children.flatMap((c, i) => (i === 0 ? [c] : [h("div", { class: "bp-repeater__separator", style: { "border-top": `1px solid ${theme.colors.border}` } }), c]))
      : children;
    return h(
      "div",
      { class: "bp-repeater", style: cssText(css) },
      // Design mode draws one iteration and the badge has to admit it, or a
      // three-item repeater and a thirty-item one look identical on the canvas.
      mode === "design"
        ? h("div", { class: "bp-repeater__badge", contenteditable: "false" }, b ? `${b.label} · ${children.length >= count ? `repeats ${count} time${count === 1 ? "" : "s"}` : `showing ${children.length} of ${count} sample items`}` : "Choose a list to repeat over")
        : null,
      h("div", { class: "bp-stack bp-repeater__items", style: { gap: `${gap}px` }, "data-dropzone": node.nodeId }, items.length === 0 && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop blocks to repeat here") : items)
    );
  }
});
